import Big from 'big.js';

export const removeComma = (str: string) => str.replace(/,/g, '');

export const extractSingleByteNumericSymbols = (str: string) => {
  return str.replace(/[^0-9+\-*/().\s]|[\u3000]/g, '');
};

const checkFormulaString = (str: string) => {
  const compressedStr = str.replace(/[\s,]/g, '');

  const operatorsOnlyRegex = /^[+\-*/()]+$/;
  if (operatorsOnlyRegex.test(compressedStr)) {
    return null;
  }

  const regex = /^[0-9()+\-*/.\s]+$/;
  const test = regex.test(compressedStr);

  if (!test) {
    return null;
  }
  return compressedStr;
};

const divideFormula = (str: string) => {
  const regex = /([+\-*/()])|(\d+(\.\d+)?)/g;
  const matches = str.match(regex);

  if (!matches) {
    return null;
  }
  return matches;
};

const compute = (expression: Array<number | string>): number | null => {
  const stack: Big[] = [];

  for (const element of expression) {
    if (typeof element === 'number') {
      try {
        stack.push(Big(element));
      } catch {
        // return null if `Error: [big.js] Invalid xxx` occurs
        return null;
      }
    } else if (typeof element === 'string') {
      if (['+', '-', '*', '/'].includes(element)) {
        const b = stack.pop();
        const a = stack.pop();
        let result = new Big(0);

        if (!a || !b) {
          return null;
        }
        try {
          switch (element) {
            case '+': {
              result = a.add(b);
              break;
            }
            case '-': {
              result = a.sub(b);
              break;
            }
            case '*': {
              result = a.times(b);
              break;
            }
            case '/': {
              const _result = a.div(b);
              if (_result === null) {
                return null;
              }
              result = _result;
              break;
            }
          }
        } catch {
          // return null if `Error: [big.js] Invalid xxx` occurs
          return null;
        }
        stack.push(result);
      }
    }
  }

  if (stack.length !== 1) {
    return null;
  }

  return stack[0]?.toNumber() ?? null;
};

const evaluate = (data: Array<number | string>): number | null => {
  const output: Array<number | string> = [];
  const operators: string[] = [];

  const priority = {
    '+': 1,
    '-': 1,
    '*': 2,
    '/': 2,
  } as const satisfies Record<string, number>;

  for (const element of data) {
    if (typeof element === 'number') {
      output.push(element);
    } else if (typeof element === 'string') {
      if (['+', '-', '*', '/'].includes(element)) {
        while (
          operators.length > 0 &&
          operators[operators.length - 1] !== '(' &&
          operators[operators.length - 1] !== undefined &&
          priority[operators[operators.length - 1] ?? ''] >= priority[element]
        ) {
          output.push(operators.pop() as string);
        }
        operators.push(element);
      } else if (element === '(') {
        operators.push(element);
      } else if (element === ')') {
        while (
          operators.length > 0 &&
          operators[operators.length - 1] !== '('
        ) {
          output.push(operators.pop() as string);
        }
        operators.pop();
      }
    }
  }

  while (operators.length > 0) {
    output.push(operators.pop() as string);
  }

  return compute(output);
};

export const calcFormulaString = (text: string): number | null => {
  const formula = checkFormulaString(text);
  if (!formula) {
    return null;
  }

  const elements = divideFormula(formula);
  if (!elements) {
    return null;
  }

  const stack: Array<number | string> = [];

  let open = 0;
  let close = 0;

  for (const e of elements) {
    if (/\d+/.test(e)) {
      stack.push(Number.parseFloat(e));
    } else {
      if (e === '(') {
        open++;
      } else if (e === ')') {
        close++;
      }
      stack.push(e);
    }
  }

  if (open !== close) {
    return null;
  }

  for (let i = 0; i < stack.length; i++) {
    if (
      stack[i] === '-' &&
      (i === 0 ||
        stack[i - 1] === '(' ||
        ['+', '-', '*', '/'].includes(stack[i - 1] as string))
    ) {
      stack[i + 1] = -Number.parseFloat(stack[i + 1] as string);
      stack.splice(i, 1);
    }
  }

  const result = evaluate(stack);

  return result !== null ? Math.floor(result) : null;
};
